/*
 * Copyright 2026 North Carolina State University
 */

/**
 * Merge compartment usage rows that are active across the WHOLE billing period.
 *
 * Two rows merge only when they share a compartment, unit, rate and quantity AND
 * both cover the bill period end to end. Everything else — anything that starts
 * after the bill start or ends before the bill end — stays on its own line,
 * because its span is what makes it a distinct charge.
 *
 * Billing Jan 1 – Jun 30, GH1 / cart / same rate:
 *   10 carts Dec 15 – Jul 31   ┐ both span the whole period
 *   10 carts Dec 25 – Jul 31   ┘ → one row: 20 carts Jan 1 – Jun 30
 *   10 carts Jan 20 – Jul 31     starts late  → its own row
 *   10 carts Dec 30 – Mar 30     ends early   → its own row
 *
 * A merged row is clamped to the bill period, since that is the span actually
 * being billed. Rows that do not span it are left exactly as they came in.
 */

import { priceChamberRow } from './ratePricing';

const ymdFromValue = (value) => {
  if (value == null) return null;
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null;
    // Local calendar day for Date objects (frontend uses local noon).
    return [value.getFullYear(), value.getMonth(), value.getDate()];
  }
  const str = String(value);
  const datePart = str.split('T')[0];
  const parts = datePart.split('-');
  if (parts.length === 3) {
    const y = parseInt(parts[0], 10);
    const m = parseInt(parts[1], 10) - 1;
    const d = parseInt(parts[2], 10);
    if (!Number.isNaN(y) && !Number.isNaN(m) && !Number.isNaN(d)) return [y, m, d];
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return [parsed.getFullYear(), parsed.getMonth(), parsed.getDate()];
};

const toDayNum = (value) => {
  const ymd = ymdFromValue(value);
  if (!ymd) return null;
  return Date.UTC(ymd[0], ymd[1], ymd[2]) / 86400000;
};

const fromDayNum = (dayNum) => {
  const dt = new Date(dayNum * 86400000);
  return new Date(dt.getUTCFullYear(), dt.getUTCMonth(), dt.getUTCDate(), 12, 0, 0, 0);
};

const inclusiveDays = (startDay, endDay) => Math.max(0, endDay - startDay + 1);

// Quantity is part of the key: two full-span rows only merge when they carry the
// same quantity as each other, not merely the same compartment and rate.
const groupKey = (row) => {
  const chamber = row.chamberId || row.chamber_id || row.chamber?._id || row.chamber?.chamber || row.chamberName || '';
  const unit = row.unitName || row.unit_name || row.unit?.base_unit || row.unit?.unit_short || '';
  const rateId = row.rateId || row.rate_id || '';
  const rate = Number(row.rate) || 0;
  const quantity = Number(row.quantity) || 0;
  const basis = row.rateBasis || row.rate_basis || '';
  return `${String(chamber)}|${String(unit)}|${String(rateId)}|${rate}|${quantity}|${basis}`;
};

const passThrough = (row) => ({
  ...row,
  sourceKeys: row.uniqueKey ? [row.uniqueKey] : [],
  sourceAssignmentIds: row.assignmentId ? [row.assignmentId] : [],
  isConsolidated: false
});

/**
 * @param {Array} rows - compartment usage rows (frontend unitCosts or backend getChamberUsage)
 * @param {Object} [options]
 * @param {*} [options.billStart] - bill period start; without it nothing merges
 * @param {*} [options.billEnd]   - bill period end; without it nothing merges
 * @param {string} [options.startField='startDate']
 * @param {string} [options.endField='endDate']
 * @param {Function} [options.makeDate] - convert day-number back to a date value
 * @returns {Array} consolidated rows
 */
export const consolidateChamberUsage = (rows, options = {}) => {
  if (!Array.isArray(rows) || rows.length === 0) return [];

  const startField = options.startField || 'startDate';
  const endField = options.endField || 'endDate';
  const makeDate = options.makeDate || fromDayNum;

  const billStartDay = toDayNum(options.billStart);
  const billEndDay = toDayNum(options.billEnd);

  // Without a bill period there is no "spans the whole period" to test, so leave
  // every row alone rather than guessing at a merge.
  const havePeriod = billStartDay != null && billEndDay != null && billEndDay >= billStartDay;
  if (!havePeriod) return rows.filter(Boolean).map(passThrough);

  const periodDays = inclusiveDays(billStartDay, billEndDay);

  // Full-span rows are grouped for merging; every other row passes straight
  // through, keeping its own dates.
  const groups = new Map();
  const result = [];

  rows.forEach((row) => {
    if (!row) return;
    const start = toDayNum(row[startField] ?? row.start_date);
    const end = toDayNum(row[endField] ?? row.end_date);
    const spansPeriod =
      start != null && end != null && start <= billStartDay && end >= billEndDay;

    if (!spansPeriod) {
      result.push(passThrough(row));
      return;
    }
    const key = groupKey(row);
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(row);
  });

  for (const group of groups.values()) {
    const template = group[0];
    const quantity = group.reduce((sum, r) => sum + (Number(r.quantity) || 0), 0);
    const startDate = makeDate(billStartDay);
    const endDate = makeDate(billEndDay);
    // Price across the merged span from the rate schedule when the rows carry
    // one (a price change inside the bill period); otherwise a flat rate.
    const priced = priceChamberRow({ ...template, quantity }, startDate, endDate);
    const rate = priced.rate;
    const totalCost = priced.totalCost;
    const sourceKeys = [...new Set(group.map(r => r.uniqueKey).filter(Boolean))];
    const sourceAssignmentIds = [...new Set(group.map(r => r.assignmentId).filter(Boolean))];
    const anyActive = group.some(r => !r.assignmentStatus || r.assignmentStatus === 'Active');

    result.push({
      ...template,
      uniqueKey: sourceKeys.length
        ? `merged_${sourceKeys.join('_')}`
        : `merged_${billStartDay}_${billEndDay}_${quantity}`,
      quantity,
      rate,
      ...(template.priceSchedule ? { priceSegments: priced.segments } : {}),
      startDate,
      endDate,
      start_date: startDate,
      end_date: endDate,
      daysUsed: periodDays,
      total_days: periodDays,
      billedUnits: priced.units,
      totalCost,
      total_cost: totalCost,
      sourceKeys,
      sourceAssignmentIds,
      isConsolidated: group.length > 1,
      isCustomized: group.some(r => r.isCustomized),
      assignmentStatus: anyActive ? 'Active' : template.assignmentStatus,
      assignmentId: sourceAssignmentIds.length === 1 ? sourceAssignmentIds[0] : sourceAssignmentIds
    });
  }

  result.sort((a, b) => {
    const aStart = toDayNum(a.startDate ?? a.start_date) || 0;
    const bStart = toDayNum(b.startDate ?? b.start_date) || 0;
    if (aStart !== bStart) return aStart - bStart;
    const aName = a.chamberName || a.chamber?.chamber || '';
    const bName = b.chamberName || b.chamber?.chamber || '';
    return String(aName).localeCompare(String(bName));
  });

  return result;
};
